// ============================================================================
// Facebook Messenger — capability glue.
//
// Bridges the provider-specific helpers in `send.ts` + `profile.ts` into the
// shape the capability registry expects (`AdapterMethods<'messaging'>`).
//
// The caller (main agent, at module init) will:
//   import { buildFacebookMessagingMethods } from './capability-methods'
//   register('facebook-messenger', 'messaging', buildFacebookMessagingMethods({}))
//
// The `ctx` argument on each method is the `AdapterContext` the dispatcher
// hands in — carrying the decrypted access token + per-instance config JSON.
// ============================================================================

import type { AdapterMethods } from '../../services/capability-registry'
import { fetchUserProfile as rawFetchUserProfile } from './profile'
import { sendMessage as rawSendMessage, sendTyping as rawSendTyping } from './send'

// ============================================================================
// Dependencies
// ============================================================================

export interface FacebookMessagingMethodsDeps {
  /**
   * Injectable fetch for tests. Defaults to the global `fetch`. Keep this as
   * the ONLY moving part so registration in production stays trivial.
   */
  fetchImpl?: typeof fetch
}

// ============================================================================
// buildFacebookMessagingMethods
// ============================================================================

export function buildFacebookMessagingMethods(
  deps: FacebookMessagingMethodsDeps = {},
): AdapterMethods<'messaging'> {
  const fetchImpl = deps.fetchImpl ?? fetch
  return {
    async sendMessage(args, ctx) {
      return rawSendMessage(
        {
          to: args.to,
          text: args.text,
          attachments: args.attachments,
        },
        ctx.accessToken,
        fetchImpl,
      )
    },
    async sendTyping(args, ctx) {
      await rawSendTyping(
        { to: args.to, state: args.state },
        ctx.accessToken,
        fetchImpl,
      )
    },
    async fetchUserProfile(args, ctx) {
      return rawFetchUserProfile(
        {
          userId: args.userId,
          pageId:
            readOpaqueString(ctx.config, 'pageId') ??
            readOpaqueString(ctx.metadata, 'pageId'),
        },
        ctx.accessToken,
        fetchImpl,
      )
    },
  }
}

function readOpaqueString(input: unknown, key: string): string | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined
  const value = (input as Record<string, unknown>)[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}
